import { useReducer, useEffect } from 'react';
import type { FormFieldMapping } from '../types/fieldMapping';
import type { FormSectionDividerEntry } from '../types/layout';

export type DynamicFieldEntry = FormFieldMapping | FormSectionDividerEntry;

interface DynamicFieldsState {
  fields: DynamicFieldEntry[];
  loading: boolean;
  error: string | null;
}

type DynamicFieldsAction =
  | { type: 'LOADING' }
  | { type: 'LOADED'; fields: DynamicFieldEntry[] }
  | { type: 'ERROR'; error: string };

function reducer(state: DynamicFieldsState, action: DynamicFieldsAction): DynamicFieldsState {
  switch (action.type) {
    case 'LOADING': return { ...state, loading: true, error: null };
    case 'LOADED': return { fields: action.fields, loading: false, error: null };
    case 'ERROR': return { ...state, loading: false, error: action.error };
  }
}

const dynamicFieldsCache = new Map<string, {
  fields: DynamicFieldEntry[];
  promise: Promise<DynamicFieldEntry[]>;
}>();

function cacheKey(actionId: string, params?: Record<string, string>): string {
  return params ? `${actionId}:${JSON.stringify(params)}` : actionId;
}

/**
 * Fetches dynamic field definitions from an ActionRegistry data action.
 * Results are cached per (actionId + params) to avoid redundant calls on re-render.
 *
 * Returns `{ fields, loading, error }`. When `actionId` is undefined (static fields),
 * returns empty fields immediately with no fetch.
 */
export function useDynamicFields(
  actionId: string | undefined,
  params?: Record<string, string>,
): DynamicFieldsState {
  const key = actionId ? cacheKey(actionId, params) : '';
  const cached = actionId ? dynamicFieldsCache.get(key) : undefined;

  const [state, dispatch] = useReducer(reducer, {
    fields: cached?.fields ?? [],
    loading: !!actionId && !cached?.fields.length,
    error: null,
  });

  useEffect(() => {
    if (!actionId) return;
    let cancelled = false;

    const existing = dynamicFieldsCache.get(key);
    if (existing?.fields.length) {
      dispatch({ type: 'LOADED', fields: existing.fields });
      return;
    }

    dispatch({ type: 'LOADING' });

    const fetchPromise = existing?.promise ?? (async () => {
      const { ActionRegistry } = await import('@packages/table-builder/registry');
      const result = await ActionRegistry.executeData(actionId, params ?? {});
      const raw = result as Record<string, unknown>;
      const fields = (raw.fields as DynamicFieldEntry[]) ?? [];
      dynamicFieldsCache.set(key, { fields, promise: Promise.resolve(fields) });
      return fields;
    })();

    if (!existing) {
      dynamicFieldsCache.set(key, { fields: [], promise: fetchPromise });
    }

    fetchPromise
      .then((fields) => { if (!cancelled) dispatch({ type: 'LOADED', fields }); })
      .catch((err) => { if (!cancelled) dispatch({ type: 'ERROR', error: String(err) }); });

    return () => { cancelled = true; };
  }, [actionId, key]);

  return state;
}

/** Clear all cached dynamic fields. Useful in tests. */
export function clearDynamicFieldsCache(): void {
  dynamicFieldsCache.clear();
}
